import React from "react";
import {
  MobileScreenSettingsWithRelations,
  UpdateMobileScreenSettings,
} from "@/types/mobile-settings";
import { ReorderableSection } from "@/components/generic/ReorderableSection";
import { CarouselItem } from "./CarouselItem";
import { InputUnderline } from "@/components/ui/input-underline";
import { Button } from "@/components/ui/button";
import { Image as ImageIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { fileService } from "@/services/file-service";
import ImageSelector from "../../reservations/guests/components/form/ImageSelector";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import mobileAppThemes from "@/data/mobile-app-themes.json";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ChatDisplaySettings } from "./ChatDisplaySettings";

const CUSTOM_THEME_TYPE = "CUSTOM";
const DEFAULT_APP_TEXT_COLOR = "#111827";
const DEFAULT_APP_BG_COLOR = "#FFFFFF";

interface MobileAppTheme {
  key: string;
  labelKey: string;
  appTextColor: string;
  appBgColor: string;
}

const themes = mobileAppThemes as MobileAppTheme[];

interface CustomizationForm {
  hotelName: string;
  logoId: string;
  carouselPhotoIds: string[];
  themeMode: "preset" | "custom";
  themeType: string;
  appTextColor: string;
  appBgColor: string;
}

interface CustomizationSettingsProps {
  settings: MobileScreenSettingsWithRelations;
  onUpdate: (data: UpdateMobileScreenSettings) => Promise<unknown>;
  isUpdating: boolean;
  onPreviewChange: (settings: MobileScreenSettingsWithRelations) => void;
  showChatDisplay?: boolean;
}

const findTheme = (themeType?: string | null) =>
  themes.find((theme) => theme.key === themeType);

const getDefaultValues = (
  settings: MobileScreenSettingsWithRelations,
): CustomizationForm => {
  const selectedTheme = findTheme(settings.themeType);
  const themeMode = selectedTheme ? "preset" : "custom";

  return {
    hotelName: settings.hotel?.name || "",
    logoId: settings.hotel?.logoId || "",
    carouselPhotoIds:
      settings.hotel?.carouselPhotos?.map((photo) => photo.photoId) || [],
    themeMode,
    themeType: selectedTheme?.key || themes[0]?.key || CUSTOM_THEME_TYPE,
    appTextColor:
      settings.appTextColor ||
      selectedTheme?.appTextColor ||
      DEFAULT_APP_TEXT_COLOR,
    appBgColor:
      settings.appBgColor || selectedTheme?.appBgColor || DEFAULT_APP_BG_COLOR,
  };
};

export const CustomizationSettings: React.FC<CustomizationSettingsProps> = ({
  settings,
  onUpdate,
  isUpdating,
  onPreviewChange,
  showChatDisplay = true,
}) => {
  const { t } = useTranslation();
  const { register, handleSubmit, setValue, watch, reset } =
    useForm<CustomizationForm>({
      defaultValues: getDefaultValues(settings),
    });
  const [chatServices, setChatServices] = React.useState(
    settings.mobileScreenSettingsChatServices ?? [],
  );
  const [isChatDisplayEnabled, setIsChatDisplayEnabled] = React.useState(
    settings.isChatDisplayEnabled ?? true,
  );

  const logoId = watch("logoId");
  const carouselPhotoIds = watch("carouselPhotoIds");
  const themeMode = watch("themeMode");
  const themeType = watch("themeType");
  const appTextColor = watch("appTextColor");
  const appBgColor = watch("appBgColor");
  const selectedTheme = findTheme(themeType);
  const effectiveTextColor =
    themeMode === "preset"
      ? selectedTheme?.appTextColor || DEFAULT_APP_TEXT_COLOR
      : appTextColor || DEFAULT_APP_TEXT_COLOR;
  const effectiveBgColor =
    themeMode === "preset"
      ? selectedTheme?.appBgColor || DEFAULT_APP_BG_COLOR
      : appBgColor || DEFAULT_APP_BG_COLOR;

  React.useEffect(() => {
    reset(getDefaultValues(settings));
    setChatServices(settings.mobileScreenSettingsChatServices ?? []);
    setIsChatDisplayEnabled(settings.isChatDisplayEnabled ?? true);
  }, [reset, settings]);

  React.useEffect(() => {
    onPreviewChange({
      ...settings,
      themeType: themeMode === "custom" ? CUSTOM_THEME_TYPE : themeType,
      appTextColor: effectiveTextColor,
      appBgColor: effectiveBgColor,
      isChatDisplayEnabled,
      mobileScreenSettingsChatServices: chatServices,
    });
  }, [
    appBgColor,
    appTextColor,
    effectiveBgColor,
    effectiveTextColor,
    chatServices,
    isChatDisplayEnabled,
    onPreviewChange,
    settings,
    themeMode,
    themeType,
  ]);

  const handleAddPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const newPhotoId = await fileService.uploadFileWithPresignedUrl(file);
        setValue("carouselPhotoIds", [...carouselPhotoIds, newPhotoId]);
      } catch (error) {
        console.error("Failed to upload photo:", error);
        toast.error(t("mobileSettings.customization.photoUploadError"));
      }
    }
  };

  const handleSave = async (data: CustomizationForm) => {
    const chosenTheme = findTheme(data.themeType);
    const isCustomTheme = data.themeMode === "custom";
    const resolvedTextColor = isCustomTheme
      ? data.appTextColor
      : chosenTheme?.appTextColor || DEFAULT_APP_TEXT_COLOR;
    const resolvedBgColor = isCustomTheme
      ? data.appBgColor
      : chosenTheme?.appBgColor || DEFAULT_APP_BG_COLOR;

    try {
      await onUpdate({
        hotelId: settings.hotelId,
        screenType: settings.screenType,
        hotelName: data.hotelName,
        logoId: data.logoId,
        carouselPhotoIds: data.carouselPhotoIds,
        themeType: isCustomTheme ? CUSTOM_THEME_TYPE : data.themeType,
        appTextColor: resolvedTextColor,
        appBgColor: resolvedBgColor,
        isChatDisplayEnabled,
        chatServiceIds: chatServices.map((item) => item.serviceId),
      });
      toast.success(t("mobileSettings.customization.updateSuccess"));
    } catch {
      toast.error(t("mobileSettings.customization.updateError"));
    }
  };

  const carouselItems = carouselPhotoIds.map((id) => ({ photoId: id }));

  return (
    <div className="space-y-12 py-6">
      {/* Logo Section */}
      <div className="flex flex-col items-center gap-4">
        <ImageSelector
          defaultIcon={
            <div className="relative flex size-32 flex-col items-center justify-center overflow-hidden rounded-lg border-2 border-zinc-900 bg-white">
              <ImageIcon className="size-8 text-zinc-400" />
              <span className="mt-2 text-sm font-bold">
                {t("mobileSettings.customization.logo")}
              </span>
            </div>
          }
          selectedImage={logoId}
          onImageSelect={() => {}}
          imageClassName="size-32 object-contain"
          overlayContent={<div></div>}
        />
      </div>

      {/* Hotel Name Section */}
      <div className="space-y-2">
        <Label>{t("mobileSettings.customization.hotelName")}</Label>
        <InputUnderline
          {...register("hotelName")}
          placeholder={t("mobileSettings.customization.hotelNamePlaceholder")}
          className="text-xl font-bold"
          isEditing={true}
          disabled={true}
        />
      </div>

      <div className="space-y-4">
        <div>
          <h3 className="text-xl font-bold">
            {t("mobileSettings.customization.appTheme")}
          </h3>
          <p className="text-sm text-zinc-500">
            {t("mobileSettings.customization.appThemeDescription")}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Button
            type="button"
            variant={themeMode === "preset" ? "new_secondary" : "new_outline"}
            className="w-full"
            onClick={() => setValue("themeMode", "preset")}
          >
            {t("mobileSettings.customization.presetTheme")}
          </Button>
          <Button
            type="button"
            variant={themeMode === "custom" ? "new_secondary" : "new_outline"}
            className="w-full"
            onClick={() => setValue("themeMode", "custom")}
          >
            {t("mobileSettings.customization.customTheme")}
          </Button>
        </div>

        {themeMode === "preset" ? (
          <div className="space-y-2">
            <Label htmlFor="mobile-app-theme">
              {t("mobileSettings.customization.theme")}
            </Label>
            <Select
              value={themeType}
              onValueChange={(value) => setValue("themeType", value)}
            >
              <SelectTrigger
                id="mobile-app-theme"
                className="w-full justify-between"
              >
                <SelectValue
                  placeholder={t("mobileSettings.customization.selectTheme")}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {themes.map((theme) => (
                    <SelectItem key={theme.key} value={theme.key}>
                      {t(theme.labelKey)}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            <Input
              type="color"
              label={t("mobileSettings.customization.textColor")}
              value={appTextColor}
              onChange={(event) => setValue("appTextColor", event.target.value)}
              className="h-12 cursor-pointer p-1"
            />
            <Input
              type="color"
              label={t("mobileSettings.customization.backgroundColor")}
              value={appBgColor}
              onChange={(event) => setValue("appBgColor", event.target.value)}
              className="h-12 cursor-pointer p-1"
            />
          </div>
        )}
      </div>

      {showChatDisplay ? (
        <ChatDisplaySettings
          hotelId={settings.hotelId}
          isEnabled={isChatDisplayEnabled}
          items={chatServices}
          onEnabledChange={setIsChatDisplayEnabled}
          onItemsChange={setChatServices}
        />
      ) : null}

      {/* Photos Carousel */}
      <div className="relative">
        <ReorderableSection
          title={t("mobileSettings.customization.photoCarousel")}
          items={carouselItems}
          onReorder={(newItems) =>
            setValue(
              "carouselPhotoIds",
              newItems.map((i) => i.photoId),
            )
          }
          onAdd={() => document.getElementById("carousel-upload")?.click()}
          onDelete={(item) =>
            setValue(
              "carouselPhotoIds",
              carouselPhotoIds.filter((id) => id !== item.photoId),
            )
          }
          keyExtractor={(item) => item.photoId}
          renderItem={(item) => <CarouselItem photo={item} />}
        />
        <input
          id="carousel-upload"
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleAddPhoto}
        />
      </div>

      {/* Save Button */}
      <div className="flex justify-center pt-8">
        <Button
          variant="new_default"
          size="xl"
          onClick={handleSubmit(handleSave)}
          disabled={isUpdating}
        >
          {isUpdating
            ? t("mobileSettings.customization.saving")
            : t("common.save")}
        </Button>
      </div>
    </div>
  );
};
